import React from 'react';
import CustomLegend from 'components/MapLegend/CustomLegend';

import settings from 'front-settings';
import pedSVG from 'assets/img/trek.svg';
import cycSVG from 'assets/img/cycling.svg';
import equSVG from 'assets/img/horse-riding.svg';
import areaSVG from 'assets/img/area.svg';
import pathwaySVG from 'assets/img/pathway.svg';

import * as sourceLayer from 'helpers/mapFiltersHelpers';

export const drawStyles = {
  default: '#ffffff',
  green45: '#b7eb8f',
  yellow45: '#ffe58f',
  darkYellow45: '#f9a718',
  red45: '#ff4433',
  darkRed45: '#950c00',
  blue: '#1da1f2',
  blueLight: '#6ebff2',
  cyc: '#1db365',
  equ: '#a47dbe',
  ped: '#f4a300',
  conflicts: '#ff4433',
};

export const terraDrawMapConfig = {
  geojsonPaint: {
    fillPaint: {
      'fill-color': drawStyles.blue,
      'fill-opacity': 0.3,
    },
    linePaint: {
      'line-color': drawStyles.blue,
      'line-opacity': 1,
      'line-width': 2,
    },
    linePaintDashed: {
      'line-color': drawStyles.blue,
      'line-opacity': 0.3,
      'line-width': 1,
      'line-dasharray': [2, 1],
    },
    routedLinePaint: {
      'line-color': drawStyles.blueLight,
      'line-opacity': 0.8,
      'line-width': 4,
    },
    routedLinePaintForbidden: {
      'line-color': drawStyles.darkRed45,
      'line-opacity': 0.8,
      'line-width': 4,
    },
    circlePaint: {
      'circle-radius': 7,
      'circle-color': drawStyles.blue,
      'circle-opacity': 0.45,
      'circle-stroke-width': 2,
      'circle-stroke-color': drawStyles.blue,
    },
  },
  geojsonConflictsPaint: {
    fillPaint: {
      'fill-color': drawStyles.conflicts,
      'fill-opacity': 0.3,
    },
    routedLinePaint: {
      'line-color': drawStyles.conflicts,
      'line-opacity': 0.8,
      'line-width': 4,
    },
    circlePaint: {
      'circle-radius': 7,
      'circle-color': drawStyles.conflicts,
      'circle-opacity': 0.45,
      'circle-stroke-width': 2,
      'circle-stroke-color': drawStyles.conflicts,
    },
  },
  drawStyles: [
    // Points
    {
      id: 'highlight-active-points',
      type: 'circle',
      filter: ['all',
        ['==', '$type', 'Point'],
        ['==', 'meta', 'feature'],
        ['==', 'active', 'true']],
      paint: {
        'circle-radius': 7,
        'circle-color': drawStyles.blue,
        'circle-stroke-width': 2,
        'circle-stroke-color': '#ffffff',
        'circle-opacity': 1,
      },
    },
    {
      id: 'points-are-blue',
      type: 'circle',
      filter: ['all',
        ['==', '$type', 'Point'],
        ['==', 'meta', 'feature'],
        ['==', 'active', 'false']],
      paint: {
        'circle-radius': 7,
        'circle-color': drawStyles.blue,
        'circle-opacity': 0.45,
        'circle-stroke-width': 2,
        'circle-stroke-color': drawStyles.blue,
      },
    },
    // ACTIVE (being drawn)
    // line stroke
    {
      id: 'gl-draw-line',
      type: 'line',
      filter: ['all', ['==', '$type', 'LineString'], ['!=', 'mode', 'static']],
      layout: {
        'line-cap': 'round',
        'line-join': 'round',
      },
      paint: {
        'line-color': drawStyles.blue,
        'line-dasharray': [0.2, 2],
        'line-width': 2,
      },
    },
    // polygon midpoints
    {
      id: 'gl-draw-polygon-midpoint',
      type: 'circle',
      filter: ['all', ['==', '$type', 'Point'],
        ['==', 'meta', 'midpoint'],
      ],
      paint: {
        'circle-radius': 3,
        'circle-color': drawStyles.blue,
      },
    },
    // polygon fill
    {
      id: 'gl-draw-polygon-fill',
      type: 'fill',
      filter: ['all', ['==', '$type', 'Polygon'], ['!=', 'mode', 'static']],
      paint: {
        'fill-color': drawStyles.blue,
        'fill-outline-color': drawStyles.blue,
        'fill-opacity': 0.1,
      },
    },
    // polygon outline stroke
    // This doesn't style the first edge of the polygon, which uses the line stroke styling instead
    {
      id: 'gl-draw-polygon-stroke-active',
      type: 'line',
      filter: ['all', ['==', '$type', 'Polygon'], ['!=', 'mode', 'static']],
      layout: {
        'line-cap': 'round',
        'line-join': 'round',
      },
      paint: {
        'line-color': drawStyles.blue,
        'line-dasharray': [0.2, 2],
        'line-width': 2,
      },
    },
    // vertex point halos
    {
      id: 'gl-draw-polygon-and-line-vertex-halo-active',
      type: 'circle',
      filter: ['all', ['==', 'meta', 'vertex'], ['==', '$type', 'Point'], ['!=', 'mode', 'static']],
      paint: {
        'circle-radius': 5,
        'circle-color': '#FFF',
      },
    },
    // vertex points
    {
      id: 'gl-draw-polygon-and-line-vertex-active',
      type: 'circle',
      filter: ['all', ['==', 'meta', 'vertex'], ['==', '$type', 'Point'], ['!=', 'mode', 'static']],
      paint: {
        'circle-radius': 3,
        'circle-color': drawStyles.blue,
      },
    },

    // INACTIVE (static, already drawn)
    // line stroke
    {
      id: 'gl-draw-line-static',
      type: 'line',
      filter: ['all', ['==', '$type', 'LineString'], ['==', 'mode', 'static']],
      layout: {
        'line-cap': 'round',
        'line-join': 'round',
      },
      paint: {
        'line-color': '#000',
        'line-width': 3,
      },
    },
    // polygon fill
    {
      id: 'gl-draw-polygon-fill-static',
      type: 'fill',
      filter: ['all', ['==', '$type', 'Polygon'], ['==', 'mode', 'static']],
      paint: {
        'fill-color': '#000',
        'fill-outline-color': '#000',
        'fill-opacity': 0.1,
      },
    },
    // polygon outline
    {
      id: 'gl-draw-polygon-stroke-static',
      type: 'line',
      filter: ['all', ['==', '$type', 'Polygon'], ['==', 'mode', 'static']],
      layout: {
        'line-cap': 'round',
        'line-join': 'round',
      },
      paint: {
        'line-color': '#000',
        'line-width': 3,
      },
    },
  ],
  sources: [
    {
      id: 'incidence',
      showFilter: true,
      options: {
        type: 'vector',
        tiles: [
          `${settings.SOURCE_VECTOR_URL}/reference/tiles/{z}/{x}/{y}/`,
        ],
        maxzoom: 16, // Set the zoom where requests to api return 204 -> empty response
      },
      layers: [
        {
          id: sourceLayer.OFF_PATHS,
          name: 'Zones d\'incidence',
          icon: areaSVG,
          iconAlt: 'Zones',
          layout: {
            visibility: 'none',
          },
          sourceLayer: sourceLayer.OFF_PATHS,
          type: 'fill',
          legendStyle: { marginRight: 8 },
          paint: {
            'fill-opacity': 0.4,
            'fill-color': '#efefef',
          },
        },
        {
          id: sourceLayer.PATHS,
          name: 'Tous les chemins',
          icon: pathwaySVG,
          iconAlt: 'Chemins',
          layout: {
            visibility: 'none',
          },
          sourceLayer: sourceLayer.PATHS,
          type: 'line',
          legendStyle: { marginRight: 8 },
          paint: {
            'line-opacity': 0.8,
            'line-width': [
              'interpolate', ['linear'], ['zoom'],
              10, 1,
              13, 2,
              18, 10,
            ],
            'line-color': drawStyles.default,
          },
        },
        {
          id: 'chemins_BAL_PED',
          sourceLayer: sourceLayer.PATHS,
          layout: {
            visibility: 'none',
          },
          name: 'Balisage pédestre',
          icon: pedSVG,
          iconAlt: 'Pédestre',
          type: 'line',
          filter: ['all', ['==', 'BAL_PED', 1]],
          legendStyle: {
            border: `solid 1px ${drawStyles.ped}`,
          },
          paint: {
            'line-opacity': 1,
            'line-width': [
              'interpolate', ['linear'], ['zoom'],
              10, 1,
              13, 2,
              18, 10,
            ],
            'line-color': drawStyles.ped,
          },
        },
        {
          id: 'chemins_AUT_CYC',
          sourceLayer: sourceLayer.PATHS,
          layout: {
            visibility: 'none',
          },
          name: 'Vélos autorisés',
          icon: cycSVG,
          iconAlt: 'Vélo',
          type: 'line',
          filter: ['any', ['==', 'AUT_CYC', 1], ['==', 'BAL_CYC', 1]],
          legendStyle: {
            border: `dashed 1px ${drawStyles.cyc}`,
          },
          paint: {
            'line-opacity': 1,
            'line-width': [
              'interpolate', ['linear'], ['zoom'],
              10, 1,
              13, 2,
              18, 10,
            ],
            'line-color': drawStyles.cyc,
            'line-dasharray': [2, 1],
          },
        },
        {
          id: 'chemins_BAL_CYC',
          sourceLayer: sourceLayer.PATHS,
          layout: {
            visibility: 'none',
          },
          name: 'Balisage vélos',
          icon: cycSVG,
          iconAlt: 'Vélo',
          type: 'line',
          filter: ['all', ['==', 'AUT_CYC', 1], ['==', 'BAL_CYC', 1]],
          legendStyle: {
            border: `solid 1px ${drawStyles.cyc}`,
          },
          paint: {
            'line-opacity': 1,
            'line-width': [
              'interpolate', ['linear'], ['zoom'],
              10, 1,
              13, 2,
              18, 10,
            ],
            'line-color': drawStyles.cyc,
          },
        },
        {
          id: 'chemins_AUT_EQU',
          sourceLayer: sourceLayer.PATHS,
          layout: {
            visibility: 'none',
          },
          name: 'Chevaux autorisés',
          icon: equSVG,
          iconAlt: 'Équestre',
          type: 'line',
          filter: ['any', ['==', 'AUT_EQU', 1], ['==', 'BAL_EQU', 1]],
          legendStyle: {
            border: `dashed 1px ${drawStyles.equ}`,
          },
          paint: {
            'line-opacity': 1,
            'line-width': [
              'interpolate', ['linear'], ['zoom'],
              10, 1,
              13, 2,
              18, 10,
            ],
            'line-color': drawStyles.equ,
            'line-dasharray': [2, 1],
          },
        },
        {
          id: 'chemins_BAL_EQU',
          sourceLayer: sourceLayer.PATHS,
          layout: {
            visibility: 'none',
          },
          name: 'Balisage équestre',
          icon: equSVG,
          iconAlt: 'Équestre',
          type: 'line',
          filter: ['all', ['==', 'AUT_EQU', 1], ['==', 'BAL_EQU', 1]],
          legendStyle: {
            border: `solid 1px ${drawStyles.equ}`,
          },
          paint: {
            'line-opacity': 1,
            'line-width': [
              'interpolate', ['linear'], ['zoom'],
              10, 1,
              13, 2,
              18, 10,
            ],
            'line-color': drawStyles.equ,
          },
        },
      ],
    },
  ],
  layout: {
    'line-join': 'round',
    'line-cap': 'round',
  },
};

export const incidenceMessages = {
  pending: { text: 'Calcul d\'incidence en cours', type: 'warning', color: '#f9a718' },

  LineString: {
    0: { text: 'Le tronçon se situe dans une zone qui n\'est pas sensible du point de vue de la préservation de l\'environnement.', type: 'success', color: '#4ac437' },
    2: { text: 'Le tronçon se situe dans une zone qui possède un niveau d\'incidence relativement élevé (niveau 2) sur l\'environnement. Des propositions alternatives pourront vous être faites.', type: 'warning', color: '#f9a718' },
    1: { text: 'Le tronçon se situe dans une zone qui possède un niveau d\'incidence relativement élevé (niveau 1) sur l\'environnement. Des propositions alternatives pourront vous être faites.', type: 'warning', color: '#f9a718' },
    3: { text: 'Le tronçon se situe dans une zone qui possède un niveau d\'incidence très élevé (niveau 3) sur l\'environnement. Vous avez de fortes chances que votre déclaration soit refusée. Nous vous recommandons donc un autre tracé.', type: 'error', color: '#ff0c00' },
    4: { text: 'Le tronçon se situe dans une zone qui possède un niveau d\'incidence très élevé (niveau 4) sur l\'environnement. Pour cette raison, vous ne pouvez pas réaliser l\'activité que vous souhaitez sur cette zone.', type: 'error', color: '#ff0c00' },
  },

  Polygon: {
    0: { text: 'La zone choisie n\'est pas sensible du point de vue de la préservation de l\'environnement.', type: 'success', color: '#4ac437' },
    1: { text: 'La zone choisie possède un niveau d\'incidence relativement élevé (niveau 1) sur l\'environnement. Des propositions alternatives pourront vous être faites.', type: 'warning', color: '#f9a718' },
    2: { text: 'La zone choisie possède un niveau d\'incidence relativement élevé (niveau 2) sur l\'environnement. Des propositions alternatives pourront vous être faites.', type: 'warning', color: '#f9a718' },
    3: { text: 'La zone choisie possède un niveau d\'incidence très élevé (niveau 3) sur l\'environnement. Vous avez de fortes chances que votre déclaration soit refusée. Nous vous recommandons donc un autre tracé.', type: 'error', color: '#ff0c00' },
    4: { text: 'La zone choisie possède un niveau d\'incidence très élevé (niveau 4) sur l\'environnement. Pour cette raison, vous ne pouvez pas réaliser l\'activité que vous souhaitez sur cette zone.', type: 'error', color: '#ff0c00' },
  },

  Point: {
    0: { text: 'Le point placé se situe dans une zone qui n\'est pas sensible du point de vue de la préservation de l\'environnement.', type: 'success', color: '#4ac437' },
    2: { text: 'Le point placé se situe dans une zone qui possède un niveau d\'incidence relativement élevé (niveau 2) sur l\'environnement. Des propositions alternatives pourront vous être faites.', type: 'warning', color: '#f9a718' },
    1: { text: 'Le point placé se situe dans une zone qui possède un niveau d\'incidence relativement élevé (niveau 1) sur l\'environnement. Des propositions alternatives pourront vous être faites.', type: 'warning', color: '#f9a718' },
    3: { text: 'Le point placé se situe dans une zone qui possède un niveau d\'incidence très élevé (niveau 3) sur l\'environnement. Vous avez de fortes chances que votre déclaration soit refusée. Nous vous recommandons donc un autre tracé.', type: 'error', color: '#ff0c00' },
    4: { text: 'Le point placé se situe dans une zone qui possède un niveau d\'incidence très élevé (niveau 4) sur l\'environnement. Pour cette raison, vous ne pouvez pas réaliser l\'activité que vous souhaitez sur cette zone.', type: 'error', color: '#ff0c00' },
  },

  chemins: {
    notAllowedPath: 'Ce tronçon passe par des zones non autorisées pour cette activité',
  },
};

export const mapLegend = [];

const mapLegendComponentItems = [
  { color: '#d3e7c0', minLabel: '0', label: 'Pas d\'incidence' },
  { color: '#f3e6c0', minLabel: '1', label: 'Relativement élevée' },
  { color: '#efcc90', minLabel: '2', label: 'Relativement élevée' },
  { color: '#f1a59b', minLabel: '3', label: 'Très élevée' },
  { color: '#c68e86', minLabel: '4', label: 'Très élevée' },
];

export const LegendComponent = <CustomLegend items={mapLegendComponentItems} />;

export const mapTitleLegend = {
  titleLegend: 'Incidence environnementale',
  title: 'Emplacement(s) concerné(s) par cette activité',
};

export const publicMessages = {
  1: { text: 'Compte tenu du nombre de personnes participant à votre activité, une étude d\'incidence sera réalisée.' },
};

export default {
  terraDrawMapConfig,
  incidenceMessages,
  publicMessages,
};



// WEBPACK FOOTER //
// ./src/components/FormMap/FormMap.config.js